import React, {Component} from "react"
import {isNull} from "lodash"

import Form from "@irati/ui/src/Form"
import {ValidationButton} from "@irati/ui/src/FormItem"
import Loading from "@irati/ui/src/Loading"
import {setForgotPassword} from "@irati/auth/services/password"
import History from "@irati/utils/history"
import {hangPromise} from "@irati/utils/promise"
import id from "@irati/utils/id"
import authentication from "@irati/auth"
import session from "@irati/auth/services/session"
import {FormattedMessage, getMessage, injectIntl, InjectIntl} from "@irati/utils/lang"
import userService from "@irati/identity/services/users"
import loginService from "@irati/auth/services/login"
import {Body} from "@irati/ui/src/Typography"
import Flex from "@irati/ui/src/Flex"
import withBranding, {WithBranding} from "@irati/brand/withBranding"
import {Branding} from "@irati/types"
import {getErrorMessage, getErrorValues} from "@irati/utils/lang/formatError"
import Alert, {AlertTypes} from "@irati/ui/src/Alert"
import LoginCodes from "@irati/auth/errorCodes/Login"
import {BusinessLogo} from "../OneTimePassword/styled"

import ResetPasswordError from "../../components/ResetPasswordError"
import PasswordSavedModal from "../../components/PasswordSavedModal/PasswordSavedModal"
import LoginBlock from "../../components/LoginBlock"
import LoginBackground from "../../components/LoginBackground"
import StyledList from "./styled/List"
import ResetPasswordInputs from "./ResetPasswordInputs"

interface Props extends InjectIntl, WithBranding {
  branding: Branding
}

interface SessionData {
  email?: string
  uuid?: string
  userId?: string
  verificationCode?: string
}

interface State {
  password?: string
  confirm?: string
  saved: boolean
  load: Promise<any>
  submit: Promise<any> | null
  postData: SessionData | null
  errorMsg: string
  errorTitle: string
}

export class ResetPassword extends Component<Props, State> {
  state = {
    password: undefined,
    confirm: undefined,
    saved: false,
    load: hangPromise(),
    submit: null,
    postData: null,
    errorTitle: "",
    errorMsg: ""
  }

  componentDidMount = () => {
    this.setState({load: this.getSessionData()})
  }

  getSessionData = () => {
    return session
      .getThenRemove(["uuid", "verificationCode"])
      .then((postData) => {
        // no user data so do not call user endpoint
        if (!postData.uuid) {
          return
        }
        return userService.unAuthedUser(postData.uuid).then(({email}) => {
          this.setState({postData: {...postData, email}})
        })
      })
      .catch(() => {
        /* no-op, fail silently */
      })
  }

  handleChange = (label: keyof State, value: string) => {
    this.setState({errorMsg: "", errorTitle: ""})
    this.setState({
      [label]: value,
      submit: null
    })
  }

  handleSubmit = () => {
    const {password, postData} = this.state

    if (!isNull(postData)) {
      const {email, uuid: userId, verificationCode: confirmation} = postData

      if (!userId || !confirmation || !password) return

      const submit = loginService.logout({redirectOnLogout: false}).then(() => {
        return setForgotPassword(userId, confirmation, password!)
          .then(() => {
            this.setState({saved: true})
            return loginService.login(authentication.unAuthedBusiness(), email, password!).then(() => {
              // Set timeout is called so user has time to see the success message before they are redirected
              setTimeout(() => {
                History.replace({
                  pathname: "/"
                })
              }, 3000)
            })
          })
          .catch((err: PBError) => {
            if (err.message.includes(LoginCodes.PasswordCriteriaFailed)) {
              this.setState({errorTitle: this.props.formatMessage({id: "platform.resetPassword.validation.alertTitle2"})})
            }
            const message = getMessage(this.props.formatMessage, getErrorMessage(err), getErrorValues(err))
            this.setState({errorMsg: message})
          })
      })

      this.setState({submit})
    }
  }

  render() {
    const {formatMessage, branding} = this.props
    const {load, submit, confirm, password, postData, errorTitle, errorMsg} = this.state
    let uuid
    let verificationCode
    let email

    if (!isNull(postData)) {
      uuid = postData.uuid
      verificationCode = postData.verificationCode
      email = postData.email
    }

    /**  containers should force to init the idNamespace */
    const idNamespace = id("reset-password", this.props, {force: true})

    const missingData = !uuid || !verificationCode || !email

    return (
      <LoginBackground>
        <Loading load={load}>
          {missingData && <ResetPasswordError />}
          {!missingData && (
            <LoginBlock id={idNamespace}>
              {errorMsg && (
                <Alert
                  type={AlertTypes.error}
                  title={errorTitle ? errorTitle : "platform.resetPassword.validation.alertTitle"}
                  subTitle={formatMessage({id: errorMsg})}
                  onHide={() => this.setState({errorMsg: ""})}
                />
              )}
              {branding.businessLogoUrl && (
                <Flex justifyContent="center" alignContent="center" marginTop>
                  <BusinessLogo src={formatMessage({id: branding.businessLogoUrl})} />
                </Flex>
              )}
              {this.state.saved ? (
                <PasswordSavedModal id={idNamespace} messageId="account.password.loggingIn" />
              ) : (
                <>
                  <Body small>
                    <FormattedMessage
                      id="platform.resetPassword.guide.legend"
                      values={{
                        boldText: (
                          <Body small display="inline" weight="bold">
                            <FormattedMessage id="platform.resetPassword.guide.boldText" />
                          </Body>
                        )
                      }}
                    />
                  </Body>
                  <StyledList>
                    <li>
                      <Body small>
                        <FormattedMessage
                          id="platform.resetPassword.guide.charLimit"
                          values={{
                            boldText: (
                              <Body small display="inline" weight="bold">
                                <FormattedMessage id="platform.resetPassword.guide.charsBold" />
                              </Body>
                            )
                          }}
                        />
                      </Body>
                    </li>
                    <li>
                      <Body small>
                        <FormattedMessage
                          id="platform.resetPassword.guide.numbers"
                          values={{
                            boldText: (
                              <Body small display="inline" weight="bold">
                                <FormattedMessage id="platform.resetPassword.guide.numbersBold" />
                              </Body>
                            )
                          }}
                        />
                      </Body>
                    </li>
                    <li>
                      <Body small>
                        <FormattedMessage
                          id="platform.resetPassword.guide.case"
                          values={{
                            boldText: (
                              <Body small display="inline" weight="bold">
                                <FormattedMessage id="platform.resetPassword.guide.caseBold" />
                              </Body>
                            )
                          }}
                        />
                      </Body>
                    </li>
                    <li>
                      <Body small>
                        <FormattedMessage id="platform.resetPassword.guide.special" />
                      </Body>
                    </li>
                  </StyledList>
                  <Form id={id("form", idNamespace)}>
                    <Flex direction="column" gap={2} width="100%">
                      <ResetPasswordInputs idNamespace={idNamespace} password={password} confirm={confirm} onChange={this.handleChange} />
                    </Flex>
                    <ValidationButton
                      className="push--top"
                      primary
                      mobileFull
                      full
                      load={submit}
                      id={id("btn-submit", idNamespace)}
                      onClick={this.handleSubmit}>
                      <FormattedMessage id="platform.resetPassword.button.resetPassword2" />
                    </ValidationButton>
                  </Form>
                </>
              )}
            </LoginBlock>
          )}
        </Loading>
      </LoginBackground>
    )
  }
}

export default injectIntl(withBranding(ResetPassword))
